import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { AclAssetPermission } from '@y42/acl/models';
import { Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { EMPTY_CHILD_NAME, EMPTY_CHILD_PATH } from '@y42/feature/shared/list-view';
import { GitQuery } from '@y42/git';
import { ImpexQuery } from '@y42/impex';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ExportTriggerQuery } from '../export-trigger/export-trigger.query';
import { ExportCTypeModel, ExportTypeContentfulService, UNKNOWN_EXPORT_TYPE_MODEL } from '../export-type';
import { ExportTypeEnum } from '../export-type.enum';
import { ExportTypeQuery } from '../export-type/export-type-query.service';
import { ExportGitService } from './export-git.service';
import {
  ExportListViewData,
  ExportSettingsGitModel,
  ExportState,
  ExportStoreModel,
  ExportUIModel,
} from './export.model';
import { ExportStore } from './export.store';

@Injectable({ providedIn: 'root' })
export class ExportQuery extends ImpexQuery<ExportState, ExportUIModel> {
  constructor(
    protected readonly store: ExportStore,
    protected readonly exportTypeQuery: ExportTypeQuery,
    protected readonly exportTriggerQuery: ExportTriggerQuery,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly gitQuery: GitQuery,
    private readonly exportGit: ExportGitService,
    private readonly exportTypeContentfulService: ExportTypeContentfulService,
  ) {
    super({
      store,
      featureType: Y42FeatureType.Export,
      toUIModel: (storeModel) => {
        const { last_used, starred } = this.getValue();
        // Don't remove optional chaining operator, in some very rare cases settings can be undefined
        const $schema = (storeModel.settings as ExportSettingsGitModel | undefined)?.$schema ?? '';
        const typeModel = this.exportTypeQuery.findModelBySchema($schema) ?? UNKNOWN_EXPORT_TYPE_MODEL;

        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.Export];
        assertNonNullable(resourceType);

        const exportHasReadPermission = !!this.featureAclQuery.getPermission({
          resourceType,
          assetPath: storeModel.$path,
          permission: AclAssetPermission.Read,
        })?.allowed;

        const triggers = storeModel.$children
          .map((id) => this.exportTriggerQuery.getUIEntity(id))
          .filter(isNotNullish)
          .map((trigger) => ({
            ...trigger,
            hasReadPermission: exportHasReadPermission,
          }));

        return {
          ...storeModel,
          featureType: Y42FeatureType.Export,
          typeIcon: typeModel.logo,
          typeLabel: typeModel.displayName,
          children: triggers,
          triggers,
          typeModel,
          last_used: last_used[storeModel.$path] || null,
          starred: !!starred[storeModel.$path],
          hasReadPermission: exportHasReadPermission,
        };
      },
    });
  }

  types$: Observable<ExportCTypeModel[] | null> = this.exportTypeContentfulService.data$.pipe(
    map((items) => {
      return (items ?? []).filter((item) => item.type !== ExportTypeEnum.UiModel);
    }),
  );

  private readonly exportTypes$ = this.exportTypeQuery.exportTypes$.pipe(filter((st) => (st || []).length > 0));

  readonly items$: Observable<ExportUIModel[]> = combineQueries([
    this.selectAll() as Observable<ExportStoreModel[]>,
    this.exportTriggerQuery.selectAll({ asObject: true }),
    this.exportTypes$,
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([exports]) => exports.map((exxport) => this.toUIModel(exxport))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly listItems$: Observable<ExportListViewData[]> = combineLatest([this.loading$, this.items$]).pipe(
    filter(([loading]) => !loading),
    map(([_, exports]) => exports.map(this.toListViewModel)),
    map((arr) => arr.flat()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly active$: Observable<ExportUIModel | undefined> = combineQueries([
    this.selectActive() as Observable<ExportStoreModel | undefined>,
    this.exportTriggerQuery.selectAll({ asObject: true }),
    this.exportTypes$,
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([exxport]) => {
      if (!exxport) {
        return undefined;
      } else {
        return this.toUIModel(exxport);
      }
    }),
    switchMap((active) =>
      active
        ? this.selectIsAuthorized(active.$path).pipe(map((isAuthorized) => ({ ...active, isAuthorized })))
        : of(undefined),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isFileType$: Observable<boolean> = this.active$.pipe(
    map((exxport) => {
      if (exxport?.typeModel?.type) {
        return [ExportTypeEnum.Excel, ExportTypeEnum.CSV].includes(exxport.typeModel?.type);
      }
      return false;
    }),
  );

  readonly activeTriggers$ = this.active$.pipe(map((exxport) => exxport?.triggers || []));

  readonly isActiveAuthorized$ = this.active$.pipe(
    switchMap((active) => (active ? this.selectIsAuthorized(active.$path) : of(false))),
    distinctUntilChanged(),
  );

  async checkForValidSettings(): Promise<boolean> {
    return true;
  }

  selectIsAuthorized($path: ExportStoreModel['$path']): Observable<boolean> {
    return combineLatest([this.gitQuery.fileTree$, this.selectEntity($path)]).pipe(
      map(() => this.exportGit.checkIfFileExists(`${$path}/.y42/secret.enc`)),
    );
  }

  readonly toListViewModel = (exxport: ExportUIModel): ExportListViewData[] => {
    const baseRow: ExportListViewData = {
      ...exxport,
      id: exxport.$path,
      rows: 0,
      child_$path: EMPTY_CHILD_PATH,
      child_name: EMPTY_CHILD_NAME,
      child_status: null,
      child_description: '',
    };

    if (!exxport.triggers.length) {
      return [baseRow];
    }

    return exxport.triggers
      .map((trigger) => this.exportTriggerQuery.getUIEntity(trigger.$path))
      .filter(isNotNullish)
      .map((triggerUIModel) => {
        const lastSuccessfulJob = triggerUIModel.latest_valid_job;
        const row: ExportListViewData = {
          ...baseRow,
          child_$path: triggerUIModel.$path,
          child_name: triggerUIModel.name,
          child_description: triggerUIModel.description,
          child_status: triggerUIModel.latest_job?.status || null,
          rows: lastSuccessfulJob?.details?.row_count || 0,
          last_import_date: lastSuccessfulJob?.finished_at ? new Date(lastSuccessfulJob?.finished_at).valueOf() : null,
        };

        return row;
      });
  };
}
